/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';
import { Anchor, Button, Grid } from '@mantine/core';
import { Link } from 'react-router-dom';
import AppStack from '../../../../common/AppStack';
import AppText from '../../../../common/AppText';
import WsCategoryCard from './WsCategoryCard';
import { PRODUCT_CATEGORY_LIST } from '../../../../../config/productCategories';
import {
  capitalizeFirstLetter,
  sterilizeUrlHandle
} from '../../../../../helpers/format';
import { LIST_SORT_ENUM } from './wsCategoryGridConfig';

const WsCategoryGridDisplay = ({
  config,
  colorSchemes,
  mediaQueries,
  ecomStoreTheme,
  onNavigate,
  baseStoreUrl,
  categoryValues,
  isSubcategoryList,
  noSort
}) => {
  const { isTabletOrSmaller } = mediaQueries;
  const colorScheme = colorSchemes[config.colorScheme - 1] ?? colorSchemes[0];
  const sortByProductCount = config.textSortBy === LIST_SORT_ENUM.PRODUCT_COUNT;

  const categories =
    (isSubcategoryList
      ? categoryValues.map((c) => ({
          category: {
            name: capitalizeFirstLetter(c),
            value: c
          },
          productCount: ecomStoreTheme.ecomStore.ecomStoreProducts.filter(
            (p) => p.productType?.toLowerCase() === c
          ).length
        }))
      : categoryValues.map((c) => ({
          category: PRODUCT_CATEGORY_LIST.find(
            (f) => f.value.toString() === c.toString()
          ),
          productCount: ecomStoreTheme.ecomStore.ecomStoreProducts.filter(
            (p) => p.fkEcomCategory === c
          ).length
        }))
    )
      .filter((c) => !!c.category)
      .sort((a, b) =>
        noSort
          ? 0
          : sortByProductCount
          ? b.productCount - a.productCount
          : a.category.name.localeCompare(b.category.name)
      ) || [];
  const routingPath = isSubcategoryList ? 'sub-categories' : 'categories';

  const gridColumnCount = isTabletOrSmaller
    ? config.mobileColumnCount * 1 ?? 1
    : config.desktopColumnCount * 1 ?? 6;

  const getColumnsSortedTopToBottom = () => {
    const perColumnBase = Math.floor(categories.length / gridColumnCount);
    const remainder = categories.length % gridColumnCount;

    let start = 0;
    const columns = Array.from({ length: gridColumnCount }, (_, colIndex) => {
      const count = perColumnBase + (colIndex < remainder ? 1 : 0); // first `remainder` columns get +1
      const slice = categories.slice(start, start + count);
      start += count;
      return slice;
    });
    return columns;
  };

  return categories.length === 0 ? (
    <AppStack style={{ alignItems: 'center' }}>
      <AppText
        style={{
          color: colorScheme.textColor,
          fontSize: 18,
          fontWeight: '500'
        }}
      >
        No {isSubcategoryList ? 'subcategories' : 'categories'} found
      </AppText>
      <Button
        color={colorScheme.solidButtonColor}
        component={Link}
        onClick={onNavigate}
        size="md"
        style={{ color: colorScheme.solidButtonLabel }}
        to={`${baseStoreUrl}/products`}
        variant="filled"
      >
        Shop all products
      </Button>
    </AppStack>
  ) : config.displayType === 'text' ? (
    <Grid columns={gridColumnCount}>
      {config.textSortBy === LIST_SORT_ENUM.ALPHABETICAL_TOP_DOWN
        ? getColumnsSortedTopToBottom().map((colItems, colIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <Grid.Col key={colIndex} span={1}>
              <Grid columns={1}>
                {colItems.map((c) => (
                  <Grid.Col
                    key={c.category.value ?? c.category.id}
                    span={1}
                    style={{ textAlign: config.textAlign ?? 'left' }}
                  >
                    <Anchor
                      component={Link}
                      onClick={onNavigate}
                      style={{ fontSize: config.fontSize * 1 ?? 18 }}
                      to={`${baseStoreUrl}/${routingPath}/${sterilizeUrlHandle(
                        c.category.name
                      )}`}
                    >
                      {c.category.name}
                    </Anchor>
                  </Grid.Col>
                ))}
              </Grid>
            </Grid.Col>
          ))
        : categories.map((c) => (
            <Grid.Col
              key={c.category.value ?? c.category.id}
              span={1}
              style={{ textAlign: config.textAlign ?? 'left' }}
            >
              <Anchor
                component={Link}
                onClick={onNavigate}
                style={{ fontSize: config.fontSize * 1 ?? 18 }}
                to={`${baseStoreUrl}/${routingPath}/${sterilizeUrlHandle(
                  c.category.name
                )}`}
              >
                {c.category.name}
              </Anchor>
            </Grid.Col>
          ))}
    </Grid>
  ) : (
    <Grid columns={gridColumnCount}>
      {config.textSortBy === LIST_SORT_ENUM.ALPHABETICAL_TOP_DOWN
        ? getColumnsSortedTopToBottom().map((colItems, colIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <Grid.Col key={colIndex} span={1}>
              <Grid columns={1}>
                {colItems.map((c) => (
                  <Grid.Col key={c.category.value} span={1}>
                    <WsCategoryCard
                      baseStoreUrl={baseStoreUrl}
                      cardSize={config.cardSize || 'large'}
                      category={c.category}
                      colorScheme={colorScheme}
                      fontSize={config.fontSize * 1}
                      iconPosition={config.iconPosition}
                      isOutlineStyle={config.isOutlineStyle === 'true'}
                      isSubcategoryList={isSubcategoryList}
                      mediaQueries={mediaQueries}
                      onNavigate={onNavigate}
                      showIcon={config.showIcons === 'true'}
                      textAlign={config.textAlign}
                    />
                  </Grid.Col>
                ))}
              </Grid>
            </Grid.Col>
          ))
        : categories.map((c) => (
            <Grid.Col key={c.category.value} span={1}>
              <WsCategoryCard
                baseStoreUrl={baseStoreUrl}
                cardSize={config.cardSize || 'large'}
                category={c.category}
                colorScheme={colorScheme}
                fontSize={config.fontSize * 1}
                iconPosition={config.iconPosition}
                isOutlineStyle={config.isOutlineStyle === 'true'}
                isSubcategoryList={isSubcategoryList}
                mediaQueries={mediaQueries}
                onNavigate={onNavigate}
                showIcon={config.showIcons === 'true'}
                textAlign={config.textAlign}
              />
            </Grid.Col>
          ))}
    </Grid>
  );
};

WsCategoryGridDisplay.propTypes = {
  baseStoreUrl: PropTypes.string,
  categoryValues: PropTypes.array,
  colorSchemes: PropTypes.array,
  config: PropTypes.object,
  ecomStoreTheme: PropTypes.object,
  isSubCategoryList: PropTypes.bool,
  isSubcategoryList: PropTypes.bool,
  mediaQueries: PropTypes.object,
  noSort: PropTypes.bool,
  onNavigate: PropTypes.func
};

export default WsCategoryGridDisplay;
